import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { resolveOwnerPlanFeatures, type OwnerPlanFeatures } from "@/lib/owner-plan-features";

export interface SubscriptionPlan {
  id: string;
  name: string;
  price_monthly: number;
  features: Record<string, boolean | number>;
}

export interface UserSubscription {
  id: string;
  status: string;
  current_period_end: string | null;
  stripe_customer_id: string | null;
}

export interface SubscriptionInvoice {
  id: string;
  stripe_invoice_id: string | null;
  amount: number;
  currency: string;
  status: string;
  invoice_date: string;
  paid_at: string | null;
}

export interface SubscriptionUsage {
  nursingHomes: { used: number; max: number };
  uploads: { used: number; max: number };
}

export function useSubscription() {
  const { user } = useAuth();

  const subscriptionQuery = useQuery({
    queryKey: ["subscription", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data: rows, error } = await supabase.rpc(
        "get_user_active_subscription",
        { user_uuid: user!.id }
      );

      if (error) throw error;

      if (!rows || rows.length === 0) return null;

      const row = rows[0];
      return {
        plan: {
          id: row.plan_id as string,
          name: row.plan_name as string,
          price_monthly: row.plan_price_monthly as number,
          features: (row.features ?? {}) as Record<string, boolean | number>,
        } as SubscriptionPlan,
        subscription: {
          id: row.subscription_id as string,
          status: row.status as string,
          current_period_end: row.current_period_end as string | null,
          stripe_customer_id: null,
        } as UserSubscription,
      };
    },
  });

  const invoicesQuery = useQuery({
    queryKey: ["subscription-invoices", user?.id],
    enabled: !!subscriptionQuery.data?.subscription?.id,
    queryFn: async () => {
      const subscriptionId = subscriptionQuery.data!.subscription!.id;
      const { data, error } = await supabase
        .from("subscription_invoices")
        .select("*")
        .eq("subscription_id", subscriptionId)
        .order("invoice_date", { ascending: false })
        .limit(12);

      if (error) throw error;
      return (data ?? []) as SubscriptionInvoice[];
    },
  });

  const usageQuery = useQuery({
    queryKey: ["subscription-usage", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const [limitsResult, nursingHomesResult] = await Promise.all([
        supabase.rpc("get_subscription_limits", { user_uuid: user!.id }),
        supabase
          .from("nursing_home_claims")
          .select("id", { count: "exact", head: true })
          .eq("user_id", user!.id)
          .eq("status", "approved"),
      ]);

      const limits = limitsResult.data?.[0];
      const usedHomes = nursingHomesResult.count ?? 0;

      return {
        nursingHomes: {
          used: usedHomes,
          max: limits?.max_nursing_homes ?? 1,
        },
        uploads: {
          used: 0,
          max: limits?.max_media_uploads ?? 20,
        },
      } as SubscriptionUsage;
    },
  });

  const plansQuery = useQuery({
    queryKey: ["subscription-plans"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscription_plans")
        .select("id, name, description, price_monthly, price_yearly, features, display_order")
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data ?? [];
    },
  });

  const plan = subscriptionQuery.data?.plan ?? null;
  const subscription = subscriptionQuery.data?.subscription ?? null;
  const subscriptionActive =
    subscription?.status === "active" || subscription?.status === "trialing";

  const ownerFeatures: OwnerPlanFeatures = useMemo(
    () =>
      resolveOwnerPlanFeatures(
        subscriptionActive,
        plan?.features as Record<string, unknown> | undefined,
      ),
    [subscriptionActive, plan?.features],
  );

  const usageAligned = useMemo((): SubscriptionUsage | null => {
    const u = usageQuery.data;
    if (!u) return null;
    return {
      nursingHomes: {
        used: u.nursingHomes.used,
        max: ownerFeatures.max_nursing_homes,
      },
      uploads: {
        used: u.uploads.used,
        max: ownerFeatures.max_media_uploads,
      },
    };
  }, [usageQuery.data, ownerFeatures]);

  const isLoading =
    subscriptionQuery.isLoading ||
    invoicesQuery.isLoading ||
    usageQuery.isLoading ||
    plansQuery.isLoading;

  return {
    plan,
    subscription,
    invoices: invoicesQuery.data ?? [],
    usage: usageAligned,
    plans: plansQuery.data ?? [],
    isLoading,
    /** Active or trialling — same rows as `get_user_active_subscription` */
    subscriptionActive,
    /** Entitlements for gating owner UI (plan JSON + free-tier defaults). */
    ownerFeatures,
    hasLeadManagement: ownerFeatures.lead_management,
    hasAdvancedAnalytics: ownerFeatures.advanced_analytics,
    hasApiAccess: ownerFeatures.api_access,
    // Convenience aliases used across the app
    subscribed: subscriptionActive,
    plan_name: plan?.name ?? null,
    plan_tier: plan?.name?.toLowerCase() ?? null,
    subscription_end: subscription?.current_period_end ?? null,
    cancel_at_period_end: false,
    loading: isLoading,
    error:
      subscriptionQuery.error ||
      invoicesQuery.error ||
      usageQuery.error ||
      plansQuery.error,
    refetch: () => {
      subscriptionQuery.refetch();
      invoicesQuery.refetch();
      usageQuery.refetch();
    },
  };
}
